import { useEffect, useState } from 'react';

import { RecoilState, useSetRecoilState } from 'recoil';

import { api_, api_session } from '@shared/plugIn/axios';
import _ from 'lodash';
import * as qs from 'qs';
import { QueryClient, QueryKey, QueryStatus, useQuery } from 'react-query';

interface QueryString {
  filter?: any;
  populate?: any;
  options?: any;
  [x: string | number | symbol]: unknown;
}
export const queryString = ({
  filter,
  populate,
  options,
  ...query
}: QueryString = {}) => {
  return qs.stringify(
    {
      ...query,
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      options: JSON.stringify(options),
    },
    { encode: false },
  );
};

export const fetcher = async ({ queryKey }: any) => {
  const { data } = await api_.get(queryKey[0]);
  return data;
};

export const fetcherSession = async ({ queryKey }: any) => {
  const { data } = await api_session.get(queryKey[0]);
  return data;
};

//* ⚠️ react-query v3는 문자열 queryKey를 배열로 감싸지 않는다.
//* fetcher가 받는 context.queryKey는 배열이지만(위 fetcher의 queryKey[0]),
//* invalidateQueries의 predicate가 받는 query.queryKey는 '문자열 그대로'다.
//* 그래서 predicate에서 query.queryKey[0]을 쓰면 URL이 아니라 첫 글자('s')가 잡혀
//* 아무것도 무효화되지 않는다. URL 조각으로 무효화할 땐 반드시 이 헬퍼를 쓸 것.
export const queryKeyUrl = (queryKey: QueryKey): string => {
  if (typeof queryKey === 'string') return queryKey;
  if (Array.isArray(queryKey) && typeof queryKey[0] === 'string') return queryKey[0];
  return '';
};

//* URL에 urlPart가 들어간 쿼리를 한 번에 무효화 (목록 쿼리가 화면마다 URL이 달라서 필요)
export const invalidateQueriesByUrlPart = (queryClient: QueryClient, urlPart: string) =>
  queryClient.invalidateQueries({
    predicate: (query) => queryKeyUrl(query.queryKey).includes(urlPart),
  });

//* 같은 기준으로 즉시 재조회 — 기본은 화면에 떠 있는(active) 쿼리만. 언마운트된 캐시까지 당기면
//* 검색 결과(수천 행 populate) 같은 큰 응답을 아무도 안 보는데 다시 받는다.
export const refetchQueriesByUrlPart = (
  queryClient: QueryClient,
  urlPart: string,
  opts: { active?: boolean } = {},
) =>
  queryClient.refetchQueries({
    predicate: (query) => queryKeyUrl(query.queryKey).includes(urlPart),
    active: opts.active ?? true,
  });

export function useRecoilQuery<T>(
  statusAtom: RecoilState<QueryStatus>,
  dataAtom: RecoilState<T>,
  url: string,
  fetcher: any,
  disabled?: boolean,
  refetchOnWindowFocus?: boolean,
) {
  const setStatus = useSetRecoilState(statusAtom);
  const setData = useSetRecoilState(dataAtom);
  const { status, data } = useQuery(url, fetcher, {
    enabled: disabled ? false : true,
    refetchOnWindowFocus: refetchOnWindowFocus ?? true,
  });

  useEffect(() => {
    setStatus(status);
  }, [status, setStatus]);

  useEffect(() => {
    if (status === 'success') {
      setData(data as T);
    }
  }, [data, setData, status]);

  return { status, data };
}

type WithId = {
  _id: string;
};

export function useRecoilQueryInterVal<T extends WithId>(
  statusAtom: RecoilState<QueryStatus>,
  dataAtom: RecoilState<T[] | null>,
  urlGenerator: (page: number) => string,
  fetcher: any,
  ms: number,
  disabled?: boolean,
) {
  const [page, setPage] = useState<number>(0);
  const [stopState, setStopState] = useState<boolean>(false);

  const setStatus = useSetRecoilState(statusAtom);
  const setData = useSetRecoilState(dataAtom);

  const { status, data } = useQuery<T[]>(urlGenerator(page), fetcher, {
    enabled: disabled ? false : true,
  });

  useEffect(() => {
    // console.log('page: ', page);
    // console.log('status: ', status);
    // console.log('data: ', data);
  }, [data, page, status]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (!stopState) {
      interval = setInterval(() => setPage((curr) => curr + 1), ms);
    }

    return () => clearInterval(interval);
  }, [ms, stopState]);

  useEffect(() => {
    setStatus((curr) => {
      if (curr === 'success' && status !== 'error') {
        return curr;
      } else {
        return status;
      }
    });
  }, [status, setStatus]);

  useEffect(() => {
    if (data?.length === 0) {
      setStopState(true);
    }
    if (status === 'success' && data) {
      setData((curr) =>
        _.unionWith(curr, data, (a, b) => {
          return a._id === b._id;
        }),
      );
    }
  }, [data, setData, status]);

  return { status, data };
}

/* ---------------------------- useQuery example ---------------------------- */
// const { status, data: funeralHomes } = useQuery(
//   `/admin/funeral-home?${useQueryString({
//     filter,
//     populate: { path: 'rooms', populate: { path: 'currentFevent' } },
//   })}`,
//   fetcher
// );

/* ---------------------------- useRecoilQuery example ---------------------------- */
// const { status, data: funeralHomes } = useRecoilQuery(
//   funeralHomesAtom,
//   `/admin/funeral-home?${useQueryString({
//     filter,
//     populate: { path: 'rooms', populate: { path: 'currentFevent' } },
//   })}`,
//   fetcher
// );
